// ============================================================
// ByOurConsent.com -- Account / Profile Page
// /account
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api   from '../utils/api';
import toast from 'react-hot-toast';

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8',
    border: '0.5px solid #c8beaa', padding: '9px 12px',
    fontSize: '14px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px',
    outline: 'none', boxSizing: 'border-box'
};
const lStyle = {
    display: 'block', fontSize: '11px',
    fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    marginBottom: '5px'
};
const SectionHeader = ({ title, sub }) => (
    <div style={{ marginBottom: '1.25rem', paddingBottom: '10px', borderBottom: '0.5px solid #c8beaa' }}>
        <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
            {title}
        </div>
        {sub && <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '3px' }}>{sub}</div>}
    </div>
);
const Field = ({ label, children }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={lStyle}>{label}</label>
        {children}
    </div>
);
const SaveBtn = ({ onClick, saving, label = 'Save changes' }) => (
    <button onClick={onClick} disabled={saving}
        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '9px 22px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: saving ? 'default' : 'pointer', borderRadius: '3px', opacity: saving ? 0.7 : 1 }}>
        {saving ? 'Saving...' : label}
    </button>
);

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

const StatusBadge = ({ status }) => {
    const colors = {
        published:      { bg: '#e8f5e0', color: '#3b6d11' },
        pending_review: { bg: '#fff3e0', color: '#8b6a3a' },
        draft:          { bg: '#ede8dc', color: '#4a5060' },
        archived:       { bg: '#f5eded', color: '#8b1a1a' },
        changes_requested: { bg: '#fff3e0', color: '#8b1a1a' }
    };
    const s = colors[status] || { bg: '#ede8dc', color: '#4a5060' };
    return (
        <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontFamily: 'sans-serif', fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
            {status?.replace(/_/g, ' ')}
        </span>
    );
};

// ============================================================
// ContributorRequestButton -- extended application form
// ============================================================
const ContributorRequestButton = ({ user }) => {
    const [status,     setStatus]     = useState(null);
    const [showForm,   setShowForm]   = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        why_contribute:  '',
        credentials:     '',
        topics:          '',
        writing_samples: '',
        website:         '',
        social:          ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    useEffect(() => {
        api.get('/users/me/contributor-request')
            .then(r => { if (r.data.request) setStatus(r.data.request.status); })
            .catch(() => {});
    }, []);

    const submit = async () => {
        if (!form.why_contribute.trim() || !form.topics.trim()) {
            toast.error('Please fill in the required fields');
            return;
        }
        setSubmitting(true);
        try {
            await api.post('/users/me/contributor-request', form);
            setStatus('pending');
            setShowForm(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to submit request');
        }
        setSubmitting(false);
    };

    if (status === 'pending') {
        return (
            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11', padding: '8px 12px', background: '#f0f5ee', border: '0.5px solid #8b9a7a', borderRadius: '3px' }}>
                ✓ Application submitted — you'll be notified when reviewed
            </div>
        );
    }

    if (showForm) {
        const iStyle = { width: '100%', background: '#fff', border: '0.5px solid #c8beaa', padding: '8px 10px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' };
        const lStyle = { fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: '4px', marginTop: '12px' };

        return (
            <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginTop: '1rem' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                    Apply to become a contributor
                </div>
                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1rem', lineHeight: 1.5 }}>
                    We review all applications before granting contributor access.
                    Fields marked * are required.
                </div>

                <label style={lStyle}>Why do you want to contribute? *</label>
                <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                    placeholder="What motivates you to write for By Our Consent? What perspective do you bring?..."
                    value={form.why_contribute}
                    onChange={e => set('why_contribute', e.target.value)} />

                <label style={lStyle}>Topics you want to write about *</label>
                <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                    placeholder="e.g. Constitutional law, state government corruption, voting rights, civil liberties..."
                    value={form.topics}
                    onChange={e => set('topics', e.target.value)} />

                <label style={lStyle}>Your background and credentials</label>
                <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                    placeholder="Professional background, education, relevant experience, area of expertise..."
                    value={form.credentials}
                    onChange={e => set('credentials', e.target.value)} />

                <label style={lStyle}>Links to previous writing (optional)</label>
                <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                    placeholder="URLs to articles, blog posts, or other published work..."
                    value={form.writing_samples}
                    onChange={e => set('writing_samples', e.target.value)} />

                <label style={lStyle}>Your website (optional)</label>
                <input style={iStyle} type="url" placeholder="https://..."
                    value={form.website}
                    onChange={e => set('website', e.target.value)} />

                <label style={lStyle}>Social media (optional)</label>
                <input style={iStyle} type="text"
                    placeholder="Twitter/X, LinkedIn, or other social media handles or URLs..."
                    value={form.social}
                    onChange={e => set('social', e.target.value)} />

                <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
                    <button onClick={submit} disabled={submitting || !form.why_contribute.trim() || !form.topics.trim()}
                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '9px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer', opacity: (submitting || !form.why_contribute.trim() || !form.topics.trim()) ? 0.6 : 1 }}>
                        {submitting ? 'Submitting...' : 'Submit application →'}
                    </button>
                    <button onClick={() => setShowForm(false)}
                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#6a7080', padding: '9px 16px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <button onClick={() => setShowForm(true)}
            style={{ background: '#1e2a3a', color: '#e8dfc8', border: 'none', padding: '8px 18px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer' }}>
            Apply to contribute →
        </button>
    );
};

// ============================================================
// AccountPage
// ============================================================
// ============================================================
const AccountPage = () => {
    const { user, loading: authLoading } = useAuth();
    const location = useLocation();
    const navigate  = useNavigate();
    const photoRef    = useRef(null);
    const featuredRef = useRef(null);

    // If someone lands here (e.g. from an email link) without an
    // active session, redirect to login instead of leaving the page
    // stuck -- every API call below assumes a logged-in user and was
    // silently failing forever otherwise. Preserve the full original
    // destination (path + query string, like ?tab=orders) so login
    // sends them right back to where they meant to go.
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login', {
                replace: true,
                state: { from: `${location.pathname}${location.search}` }
            });
        }
    }, [authLoading, user, location, navigate]);

    // Support ?tab=orders in URL or location state
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl   = searchParams.get('tab');

    const [profile,    setProfile]    = useState(null);
    const [contribProfile, setContribProfile] = useState(null);
    const [articles,   setArticles]   = useState([]);
    const [myChapters, setMyChapters] = useState([]);
    const [activeTab,  setActiveTab]  = useState(tabFromUrl || location.state?.tab || 'account');
    const [saving,     setSaving]     = useState(false);

    // Account form
    const [accountForm, setAccountForm] = useState({
        first_name: '', last_name: '', display_name: ''
    });

    // Password form
    const [pwForm, setPwForm] = useState({
        current_password: '', new_password: '', confirm_password: ''
    });

    // Contributor form
    const [contribForm, setContribForm] = useState({
        name: '', bio: '', organization: '', website_url: '',
        photo_url: '', featured_image_url: ''
    });

    const isContributor = user?.role === 'contributor';
    const isLeader      = user?.role === 'group_leader';
    const showContrib   = isContributor || isLeader || user?.role === 'admin' || user?.role === 'superadmin';

    // Subscription status
    const [subStatus,     setSubStatus]     = useState(null);
    const [cancelConfirm, setCancelConfirm] = useState(false);
    const [orders,        setOrders]        = useState([]);

    useEffect(() => {
        if (!user) return;
        api.get('/orders/my')
            .then(r => setOrders(r.data.orders || []))
            .catch(() => {});
    }, [user]);
    useEffect(() => {
        if (!user) return;
        api.get('/subscriptions/status')
            .then(r => setSubStatus(r.data))
            .catch(() => {});
    }, [user]);
    const showChapters  = true; // all logged-in users can have chapters

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const r = await api.get('/users/me');
            const u = r.data.user;
            setProfile(u);
            setAccountForm({
                first_name:   u.first_name   || '',
                last_name:    u.last_name    || '',
                display_name: u.display_name || ''
            });
            setContribForm({
                name:               u.author_name        || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                bio:                u.author_bio         || '',
                organization:       u.organization       || '',
                website_url:        u.website_url        || '',
                photo_url:          u.author_photo_url   || '',
                featured_image_url: u.author_featured_image_url || ''
            });
        } catch { toast.error('Failed to load profile'); }

        try {
            const r = await api.get('/users/me/articles');
            setArticles(r.data.articles || []);
        } catch { }

        try {
            const r = await api.get('/users/me/chapters');
            setMyChapters(r.data.chapters || []);
        } catch { }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const saveAccount = async () => {
        setSaving(true);
        try {
            await api.put('/users/me', accountForm);
            toast.success('Profile updated');
            load();
        } catch { toast.error('Failed to update profile'); }
        finally { setSaving(false); }
    };

    const savePassword = async () => {
        if (pwForm.new_password !== pwForm.confirm_password) {
            toast.error('New passwords do not match');
            return;
        }
        if (pwForm.new_password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        setSaving(true);
        try {
            await api.put('/users/me/password', {
                current_password: pwForm.current_password,
                new_password:     pwForm.new_password
            });
            toast.success('Password changed');
            setPwForm({ current_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to change password');
        }
        finally { setSaving(false); }
    };

    const [contribSaved, setContribSaved] = useState(false);

    const saveContrib = async () => {
        setSaving(true);
        try {
            await api.put('/users/me/contributor', contribForm);
            toast.success('Contributor profile saved');
            setContribSaved(true);
            setTimeout(() => setContribSaved(false), 3000);
            load();
        } catch { toast.error('Failed to save contributor profile'); }
        finally { setSaving(false); }
    };

    const uploadPhoto = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('photo', file);
        try {
            const r = await api.post('/users/me/photo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setContribForm(p => ({ ...p, photo_url: r.data.url }));
            toast.success('Photo uploaded');
        } catch { toast.error('Photo upload failed'); }
    };

    const uploadFeatured = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('image', file);
        try {
            const r = await api.post('/users/me/featured-image', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setContribForm(p => ({ ...p, featured_image_url: r.data.url }));
            toast.success('Featured image uploaded');
        } catch { toast.error('Image upload failed'); }
    };

    const tabs = [
        { key: 'account',     label: 'Account' },
        { key: 'premium',     label: subStatus?.isActive ? '⭐ Premium' : 'Premium membership' },
        { key: 'orders',      label: 'My orders' },
        ...(showContrib   ? [{ key: 'contributor', label: 'Contributor profile' }] : []),
        ...(myChapters.length > 0 || isLeader ? [{ key: 'chapters', label: `My chapters${myChapters.length > 0 ? ` (${myChapters.length})` : ''}` }] : []),
        { key: 'submissions', label: `My articles${articles.length > 0 ? ` (${articles.length})` : ''}` }
    ];

    if (!profile) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}>
            <Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>
                Loading...
            </div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>My Account — By Our Consent</title>
            </Helmet>

            <Nav />

            {/* Page header */}
            <div style={{ background: '#1e2a3a', padding: '1.25rem 1.5rem', borderBottom: '2px solid #8b1a1a' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                    <div style={{ fontSize: '22px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif" }}>
                        My Account
                    </div>
                    <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7a8a', marginTop: '2px' }}>
                        {profile.email} · {profile.role}
                    </div>
                </div>
            </div>

            {/* Tab bar */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex' }}>
                    {tabs.map(t => (
                        <button key={t.key} onClick={() => setActiveTab(t.key)}
                            style={{ background: 'transparent', border: 'none', borderBottom: activeTab === t.key ? '2px solid #8b1a1a' : '2px solid transparent', padding: '10px 18px', fontSize: '13px', fontFamily: 'sans-serif', color: activeTab === t.key ? '#1e2a3a' : '#6a7080', fontWeight: activeTab === t.key ? 600 : 400, cursor: 'pointer', marginBottom: '-1px' }}>
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                {/* ── ACCOUNT TAB ── */}
                {/* ORDERS TAB */}
                {activeTab === 'orders' && (
                    <div style={{ maxWidth: '640px' }}>
                        <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.25rem', paddingBottom: '10px', borderBottom: '0.5px solid #c8beaa' }}>
                            My orders
                        </div>

                        {orders.length === 0 ? (
                            <div style={{ padding: '2rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                                <div style={{ fontSize: '16px', fontFamily: "'EB Garamond', Georgia, serif", color: '#1e2a3a', marginBottom: '8px' }}>No orders yet</div>
                                <Link to="/store" style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none' }}>Browse the store →</Link>
                            </div>
                        ) : (
                            orders.map(order => (
                                <div key={order.id} style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                                        <div>
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                                Order #{order.order_number}
                                            </div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px' }}>
                                                {new Date(order.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                                ${(order.total_cents / 100).toFixed(2)}
                                            </div>
                                            <div style={{
                                                fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase',
                                                padding: '3px 10px', borderRadius: '20px', fontWeight: 600,
                                                background: order.status === 'shipped' || order.status === 'delivered' ? '#f0f5ee' :
                                                            order.status === 'processing' ? '#f0e8e0' : '#ede8dc',
                                                color: order.status === 'shipped' || order.status === 'delivered' ? '#3b6d11' :
                                                       order.status === 'processing' ? '#8b6a3a' : '#6a7080'
                                            }}>
                                                {order.status === 'pending' ? 'Processing' :
                                                 order.status === 'processing' ? 'In production' :
                                                 order.status === 'shipped' ? 'Shipped' :
                                                 order.status === 'delivered' ? 'Delivered' :
                                                 order.status}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Items */}
                                    {order.items && order.items.map((item, i) => (
                                        <div key={i} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', padding: '4px 0', borderTop: i === 0 ? '0.5px solid #c8beaa' : 'none' }}>
                                            {item.product_name}
                                            {item.variant_desc && <span style={{ color: '#8a8070' }}> · {item.variant_desc}</span>}
                                            {item.quantity > 1 && <span style={{ color: '#8a8070' }}> × {item.quantity}</span>}
                                        </div>
                                    ))}

                                    {/* Tracking */}
                                    {order.tracking_number && (
                                        <div style={{ marginTop: '10px', padding: '8px 12px', background: '#ede8dc', borderRadius: '3px', fontSize: '12px', fontFamily: 'sans-serif' }}>
                                            <span style={{ color: '#6a7080' }}>Tracking: </span>
                                            {order.tracking_url ? (
                                                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a', fontWeight: 600 }}>
                                                    {order.tracking_number} →
                                                </a>
                                            ) : (
                                                <span style={{ color: '#1e2a3a', fontWeight: 600 }}>{order.tracking_number}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Production time notice for pending orders */}
                                    {(order.status === 'pending' || order.status === 'processing') && (
                                        <div style={{ marginTop: '8px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', fontStyle: 'italic' }}>
                                            Items are made to order. Production takes 2-5 business days before shipping.
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* PREMIUM TAB */}
                {activeTab === 'premium' && (
                    <div style={{ maxWidth: '560px' }}>
                        {subStatus?.isActive ? (
                            <div>
                                {/* Active subscription */}
                                <div style={{ background: '#1e2a3a', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem', borderLeft: '3px solid #8b9a7a' }}>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '6px' }}>
                                        {subStatus.status === 'trialing' ? '⭐ Free trial active' : '⭐ Premium member'}
                                    </div>
                                    <div style={{ fontSize: '22px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                                        {subStatus.status === 'trialing'
                                            ? `${subStatus.daysLeft} day${subStatus.daysLeft !== 1 ? 's' : ''} remaining in your trial`
                                            : 'Active membership'}
                                    </div>
                                    {subStatus.trialEnd && (
                                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                                            {subStatus.status === 'trialing'
                                                ? `Trial ends ${new Date(subStatus.trialEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · Then $9/month`
                                                : `Next billing: ${new Date(subStatus.periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                                        </div>
                                    )}
                                </div>

                                {/* Tools access */}
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1rem' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', marginBottom: '10px', fontFamily: "'EB Garamond', Georgia, serif" }}>Your premium tools</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                        {[
                                            ['/premium/foia', '📋 FOIA Generator'],
                                            ['/premium/notices', '📜 Notice Generator'],
                                            ['/premium/bill-analysis', '🏛️ Bill Analysis'],
                                            ['/premium/constitutional-research', '⚖️ Constitutional Research'],
                                            ['/premium/rights-analyzer', '🛡️ Rights Analyzer'],
                                            ['/premium/law-checker', '📰 Law vs Action'],
                                            ['/premium/official-accountability', '🔎 Official Accountability'],
                                            ['/premium/follow-the-money', '💰 Follow the Money'],
                                            ['/premium/official-tracker', '📡 Official Tracker'],
                                        ].map(([to, label]) => (
                                            <a key={to} href={to} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none', padding: '4px 0' }}>
                                                {label} →
                                            </a>
                                        ))}
                                    </div>
                                </div>

                                {/* Cancel */}
                                {subStatus.status !== 'cancelling' && (
                                    <div style={{ padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '8px' }}>
                                            {subStatus.status === 'trialing'
                                                ? 'Cancel before your trial ends and you won\'t be charged.'
                                                : 'Cancel your membership. You\'ll keep access until the end of your billing period.'}
                                        </div>
                                        {cancelConfirm ? (
                                            <div>
                                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '8px', fontWeight: 600 }}>
                                                    Are you sure? You'll lose access to all 9 premium tools.
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                await api.post('/subscriptions/cancel');
                                                                toast.success('Membership cancelled');
                                                                const r = await api.get('/subscriptions/status');
                                                                setSubStatus(r.data);
                                                                setCancelConfirm(false);
                                                            } catch {
                                                                toast.error('Failed to cancel. Please try again.');
                                                            }
                                                        }}
                                                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                                        Yes, cancel my membership
                                                    </button>
                                                    <button onClick={() => setCancelConfirm(false)}
                                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#6a7080', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                                        Keep my membership
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button onClick={() => setCancelConfirm(true)}
                                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#6a7080', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                                Cancel membership
                                            </button>
                                        )}
                                    </div>
                                )}
                                {subStatus.status === 'cancelling' && (
                                    <div style={{ padding: '10px 14px', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                        Membership cancelled. You have access until {new Date(subStatus.trialEnd || subStatus.periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Not subscribed
                            <div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1rem' }}>
                                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                                        You don't have a premium membership yet
                                    </div>
                                    <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6, marginBottom: '1rem' }}>
                                        Get full access to all 9 civic tools for 30 days free, then $9/month.
                                        Cancel anytime before the trial ends and you won't be charged.
                                    </div>
                                    <a href="/premium/subscribe" style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                                        Start 30-day free trial →
                                    </a>
                                </div>
                                <a href="/premium" style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none' }}>
                                    Learn about premium tools →
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {/* ACCOUNT TAB */}
                {activeTab === 'account' && (
                    <div>
                        {/* Basic info */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                            <SectionHeader title="Basic information" />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <Field label="First name">
                                    <input style={iStyle} value={accountForm.first_name}
                                        onChange={e => setAccountForm(p => ({...p, first_name: e.target.value}))} />
                                </Field>
                                <Field label="Last name">
                                    <input style={iStyle} value={accountForm.last_name}
                                        onChange={e => setAccountForm(p => ({...p, last_name: e.target.value}))} />
                                </Field>
                            </div>
                            <Field label="Display name">
                                <input style={iStyle} value={accountForm.display_name}
                                    onChange={e => setAccountForm(p => ({...p, display_name: e.target.value}))}
                                    placeholder="How your name appears publicly" />
                            </Field>
                            <Field label="Email address">
                                <input style={{ ...iStyle, background: '#ede8dc', color: '#8a8070' }}
                                    value={profile.email} disabled />
                            </Field>
                            <div style={{ marginTop: '4px' }}>
                                <SaveBtn onClick={saveAccount} saving={saving} />
                            </div>
                        </div>

                        {/* Change password */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem' }}>
                            <SectionHeader title="Change password" />
                            <Field label="Current password">
                                <input style={iStyle} type="password" value={pwForm.current_password}
                                    onChange={e => setPwForm(p => ({...p, current_password: e.target.value}))} />
                            </Field>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <Field label="New password">
                                    <input style={iStyle} type="password" value={pwForm.new_password}
                                        onChange={e => setPwForm(p => ({...p, new_password: e.target.value}))}
                                        placeholder="Min. 8 characters" />
                                </Field>
                                <Field label="Confirm new password">
                                    <input style={iStyle} type="password" value={pwForm.confirm_password}
                                        onChange={e => setPwForm(p => ({...p, confirm_password: e.target.value}))} />
                                </Field>
                            </div>
                            <div style={{ marginTop: '4px' }}>
                                <SaveBtn onClick={savePassword} saving={saving} label="Change password" />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── CONTRIBUTOR TAB ── */}
                {activeTab === 'contributor' && showContrib && (
                    <div>
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                            <SectionHeader
                                title="Contributor profile"
                                sub="This information appears on your articles as the author byline. It auto-fills when you submit an article."
                            />

                            {/* Photo upload */}
                            <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '0.5px solid #c8beaa' }}>
                                <div style={{ flexShrink: 0 }}>
                                    {contribForm.photo_url ? (
                                        <img src={`http://localhost:5002${contribForm.photo_url}`}
                                            alt="Headshot"
                                            style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #c8beaa' }} />
                                    ) : (
                                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", fontWeight: 600 }}>
                                            {(profile.first_name || 'U')[0]}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div style={{ fontSize: '13px', fontFamily: 'sans-serif', fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>
                                        Headshot / Logo
                                    </div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '8px' }}>
                                        Square image recommended · 400×400px minimum · JPG or PNG
                                    </div>
                                    <button onClick={() => photoRef.current?.click()}
                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '6px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                        {contribForm.photo_url ? 'Change photo' : 'Upload photo'}
                                    </button>
                                    <input ref={photoRef} type="file" accept="image/*" onChange={uploadPhoto} style={{ display: 'none' }} />
                                </div>
                            </div>

                            {/* Byline info */}
                            <Field label="Display name / Byline">
                                <input style={iStyle} value={contribForm.name}
                                    onChange={e => setContribForm(p => ({...p, name: e.target.value}))}
                                    placeholder="How your name appears on articles" />
                            </Field>
                            <Field label="Organization">
                                <input style={iStyle} value={contribForm.organization}
                                    onChange={e => setContribForm(p => ({...p, organization: e.target.value}))}
                                    placeholder="ACLU, Brookings Institution, Independent..." />
                            </Field>
                            <Field label="Website URL">
                                <input style={iStyle} value={contribForm.website_url}
                                    onChange={e => setContribForm(p => ({...p, website_url: e.target.value}))}
                                    placeholder="https://yoursite.com" />
                            </Field>
                            <Field label="Short bio">
                                <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                    value={contribForm.bio}
                                    onChange={e => setContribForm(p => ({...p, bio: e.target.value}))}
                                    placeholder="1-2 sentences about your background and expertise..." />
                            </Field>
                        </div>

                        {/* Default featured image */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                            <SectionHeader
                                title="Default featured image"
                                sub="Used as the header image on your articles when you don't upload a specific one."
                            />
                            {contribForm.featured_image_url ? (
                                <div style={{ marginBottom: '1rem' }}>
                                    <img src={`http://localhost:5002${contribForm.featured_image_url}`}
                                        alt="Featured"
                                        style={{ width: '200px', height: '120px', objectFit: 'cover', borderRadius: '3px', border: '0.5px solid #c8beaa' }} />
                                    <div style={{ marginTop: '8px' }}>
                                        <button onClick={() => featuredRef.current?.click()}
                                            style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '6px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px', marginRight: '8px' }}>
                                            Change image
                                        </button>
                                        <button onClick={() => setContribForm(p => ({...p, featured_image_url: ''}))}
                                            style={{ background: 'transparent', border: 'none', color: '#8b1a1a', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', textDecoration: 'underline' }}>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <button onClick={() => featuredRef.current?.click()}
                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '6px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                        Upload image
                                    </button>
                                    <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginLeft: '10px' }}>
                                        Recommended 1200×630px · JPG or PNG · max 5MB
                                    </span>
                                </div>
                            )}
                            <input ref={featuredRef} type="file" accept="image/*" onChange={uploadFeatured} style={{ display: 'none' }} />
                        </div>

                        {/* Preview */}
                        {(contribForm.name || contribForm.organization) && (
                            <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                                    Byline preview
                                </div>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    {contribForm.photo_url ? (
                                        <img src={`http://localhost:5002${contribForm.photo_url}`} alt=""
                                            style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#1e2a3a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", fontSize: '18px', fontWeight: 600 }}>
                                            {(contribForm.name || 'U')[0]}
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif' }}>
                                            {contribForm.name}
                                        </div>
                                        {contribForm.organization && (
                                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                                                {contribForm.organization}
                                                {contribForm.website_url && (
                                                    <span> · <a href={contribForm.website_url} style={{ color: '#8b6a3a' }} target="_blank" rel="noopener noreferrer">
                                                        {contribForm.website_url.replace(/^https?:\/\//, '')}
                                                    </a></span>
                                                )}
                                            </div>
                                        )}
                                        {contribForm.bio && (
                                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px', fontStyle: 'italic' }}>
                                                {contribForm.bio}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <SaveBtn onClick={saveContrib} saving={saving} label="Save contributor profile" />
                            {contribSaved && (
                                <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11', fontWeight: 600 }}>
                                    ✓ Saved!
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* ── SUBMISSIONS TAB ── */}
                {activeTab === 'chapters' && showChapters && (
                    <div>
                        <div style={{ marginBottom: '1.25rem', paddingBottom: '10px', borderBottom: '0.5px solid #c8beaa' }}>
                            <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                My chapters
                            </div>
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px' }}>
                                Chapters you lead or are a member of
                            </div>
                        </div>

                        {myChapters.length === 0 ? (
                            <div style={{ padding: '3rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏛️</div>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                                    No chapters yet
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '1rem' }}>
                                    You haven't joined or started a chapter yet.
                                </div>
                                <Link to="/chapters" style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', textDecoration: 'none', borderRadius: '3px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                    Browse chapters
                                </Link>
                            </div>
                        ) : (
                            myChapters.map(c => (
                                <div key={c.id} style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderLeft: `3px solid ${c.role === 'leader' ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '4px', padding: '1.25rem', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                        <div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                                                <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                                    {c.name}
                                                </div>
                                                {c.role === 'leader' && (
                                                    <span style={{ fontSize: '9px', background: '#8b1a1a', color: '#f5f0e8', padding: '2px 6px', borderRadius: '20px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
                                                        Leader
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                {c.type} · {c.member_count} members
                                                {c.location && ` · ${c.location}`}
                                            </div>
                                            {c.pending_count > 0 && (
                                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', marginTop: '4px', fontWeight: 600 }}>
                                                    ⏳ {c.pending_count} pending membership {c.pending_count === 1 ? 'request' : 'requests'}
                                                </div>
                                            )}
                                        </div>
                                        <Link to={`/chapters/${c.slug}`}
                                            style={{ background: '#1e2a3a', color: '#e8dfc8', padding: '8px 16px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px', flexShrink: 0 }}>
                                            {c.role === 'leader' ? 'Manage chapter →' : 'View chapter →'}
                                        </Link>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {activeTab === 'submissions' && (
                    <div>
                        <div style={{ marginBottom: '1.25rem', paddingBottom: '10px', borderBottom: '0.5px solid #c8beaa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    My articles
                                </div>
                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px' }}>
                                    {articles.length} {articles.length === 1 ? 'article' : 'articles'} submitted
                                </div>
                            </div>
                            {/* Show submit button only for contributors/admins */}
                            {(isContributor || isLeader || user?.role === 'admin' || user?.role === 'superadmin') ? (
                                <Link to="/submit-article"
                                    style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '8px 18px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                                    + Submit article
                                </Link>
                            ) : (
                                <ContributorRequestButton user={user} />
                            )}
                        </div>

                        {articles.length === 0 ? (
                            <div style={{ padding: '3rem', textAlign: 'center', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📝</div>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                                    No articles yet
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                    Submit your first article for review
                                </div>
                            </div>
                        ) : (
                            articles.map((a, i) => (
                                <div key={a.id} style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '1rem', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                                                {a.status === 'published' ? (
                                                    <Link to={`/articles/${a.slug}`} style={{ color: '#1e2a3a', textDecoration: 'none' }}>
                                                        {a.title}
                                                    </Link>
                                                ) : a.title}
                                            </div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                {a.category_name && <span>{a.category_name}</span>}
                                                {a.submitted_at && <span>Submitted {fmtDate(a.submitted_at)}</span>}
                                                {a.published_at && <span>Published {fmtDate(a.published_at)}</span>}
                                            </div>
                                            {/* Editor notes */}
                                            {a.review_notes && (
                                                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fff8f0', border: '0.5px solid #d4a96a', borderRadius: '3px', borderLeft: '3px solid #8b6a3a' }}>
                                                    <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8b6a3a', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '3px' }}>
                                                        Editor notes
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a3020' }}>
                                                        {a.review_notes}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                                            <StatusBadge status={a.status} />
                                            {a.status === 'published' && (
                                                <div style={{ textAlign: 'right' }}>
                                                    {a.unique_view_count > 0 && (
                                                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#3b6d11' }}>
                                                            {a.unique_view_count.toLocaleString()} readers
                                                        </div>
                                                    )}
                                                    {a.share_count > 0 && (
                                                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                                            {a.share_count} shares
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(a.status === 'draft' || a.status === 'changes_requested') && (
                                                <Link to="/submit-article"
                                                    style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b1a1a', textDecoration: 'none' }}>
                                                    Resubmit →
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}

                        {/* Stats summary */}
                        {articles.filter(a => a.status === 'published').length > 0 && (
                            <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '4px', display: 'flex', gap: '2rem' }}>
                                {[
                                    ['Published', articles.filter(a => a.status === 'published').length],
                                    ['Total readers', articles.reduce((s, a) => s + (a.unique_view_count || 0), 0).toLocaleString()],
                                    ['Total shares', articles.reduce((s, a) => s + (a.share_count || 0), 0).toLocaleString()]
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>{val}</div>
                                        <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default AccountPage;
